import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@heroui/react';
import { isToolUIPart } from 'ai';
import type { UIMessage } from 'ai';

import { ToolCallCard } from './ToolCallCard';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useSessions } from '../hooks/useSessions';

interface MessageListProps {
  messages: UIMessage[];
  isStreaming: boolean;
  isActive: boolean;
  status: string;
  /**
   * Source sessionId for the conversation rendered here. Required when
   * the fork affordance should appear (DD-BLB-24); pass `undefined` to
   * disable forking entirely (e.g., a read-only message viewer that's
   * not bound to a real session).
   */
  sessionId?: string;
}

/**
 * Renders the conversation as a vertical scroller. Each message is
 * decomposed into its `parts` so the model's interleaved thinking,
 * text, and tool calls can stream into place. The scroller pins to the
 * bottom while new content arrives, but only when the user hasn't
 * scrolled away — respecting their scroll position is a lower-friction
 * default than forcing autoscroll.
 */
export function MessageList({
  messages,
  isStreaming,
  isActive,
  status,
  sessionId,
}: MessageListProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const { forkChat } = useSessions();
  const [forkBusy, setForkBusy] = useState<string | null>(null);
  const [forkError, setForkError] = useState<string | null>(null);

  // Track whether the user is parked near the bottom; if so, keep
  // scrolling as new content lands.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    if (!stickToBottomRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming, isActive]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center">
        <div className="max-w-md">
          <h2 className="text-lg font-semibold tracking-tight text-balance">
            Start a conversation
          </h2>
          <p className="mt-2 text-sm opacity-70 text-pretty">
            Ask Bluebird to research, draft, plan, or run code in a sandbox.
            Streaming progress and tool calls will appear here as they happen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      className="scroll-pane min-h-0 flex-1 overflow-y-auto px-4 py-6"
      aria-live="polite"
      aria-busy={isStreaming || status === 'submitted'}
    >
      <ol className="mx-auto flex max-w-3xl flex-col gap-6">
        {messages.map((m, i) => {
          // Highlight only "settled" messages. While streaming, the
          // last assistant message is mid-flight — running highlight.js
          // on every delta would re-walk the AST per chunk. We skip
          // highlighting just for that one bubble; once the turn ends
          // (`isStreaming` flips false) the bubble re-renders once with
          // full highlighting.
          const isLastMessage = i === messages.length - 1;
          const isInFlight =
            isStreaming && isLastMessage && m.role === 'assistant';
          // Fork affordance is only meaningful for messages that have
          // already been persisted to the conversation tree. A still-
          // streaming assistant message has no stable id on the server
          // yet (Session.fork accepts any saved message; the in-flight
          // delta isn't appended until the turn completes). Hide the
          // button while the bubble is mid-flight to avoid a 404 race.
          const canFork =
            sessionId !== undefined && !isInFlight && m.role !== 'system';
          return (
            <li key={m.id} className="contents">
              <MessageBubble
                message={m}
                highlight={!isInFlight}
                onFork={
                  canFork
                    ? async () => {
                        if (forkBusy) return;
                        setForkBusy(m.id);
                        setForkError(null);
                        try {
                          await forkChat(sessionId!, m.id);
                        } catch (err) {
                          setForkError(
                            err instanceof Error
                              ? err.message
                              : 'Fork failed; please try again.',
                          );
                        } finally {
                          setForkBusy(null);
                        }
                      }
                    : undefined
                }
                forkBusy={forkBusy === m.id}
              />
            </li>
          );
        })}
        {forkError && (
          <li
            role="alert"
            className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200"
          >
            {forkError}
          </li>
        )}
        {status === 'submitted' && (
          <li className="flex items-center gap-2 text-sm opacity-70">
            <Spinner size="sm" aria-label="Awaiting response" />
            <span>Bluebird is thinking…</span>
          </li>
        )}
      </ol>
    </div>
  );
}

function MessageBubble({
  message,
  highlight,
  onFork,
  forkBusy,
}: {
  message: UIMessage;
  highlight: boolean;
  onFork?: () => void;
  forkBusy?: boolean;
}) {
  const isUser = message.role === 'user';
  return (
    <article
      className={`group flex flex-col gap-2 ${isUser ? 'items-end' : 'items-start'}`}
    >
      <div
        className={[
          'flex items-center gap-2 text-xs uppercase tracking-wider opacity-60',
          isUser ? 'flex-row-reverse' : '',
        ].join(' ')}
      >
        <RoleBadge role={message.role} />
        {onFork && (
          <button
            type="button"
            onClick={onFork}
            disabled={forkBusy}
            // Fork affordance is hidden until hover so a quiet
            // conversation isn't peppered with redundant icons. Always
            // visible when the action is in flight so the user sees
            // the busy state.
            className={[
              'rounded px-1.5 py-0.5 text-[10px] tracking-normal normal-case opacity-0 transition group-hover:opacity-80 hover:opacity-100',
              forkBusy ? 'opacity-100' : '',
              'border border-white/10 hover:bg-white/5',
            ].join(' ')}
            aria-label={`Fork conversation from this message`}
            title="Fork from here — copies messages up to this point into a new chat tab"
          >
            {forkBusy ? 'Forking…' : 'Fork from here'}
          </button>
        )}
      </div>
      <div
        className={[
          'prose-msg w-full max-w-[min(48rem,90%)] rounded-2xl px-4 py-3 text-sm',
          isUser
            ? 'bg-blue-500/15 text-foreground ring-1 ring-blue-400/20'
            : 'bg-white/4 ring-1 ring-white/8',
        ].join(' ')}
      >
        {message.parts.map((part, i) => (
          <PartView
            key={`${message.id}-${i}`}
            part={part}
            renderMarkdown={!isUser}
            highlight={highlight}
          />
        ))}
      </div>
    </article>
  );
}

function RoleBadge({ role }: { role: string }) {
  switch (role) {
    case 'user':
      return <span>You</span>;
    case 'assistant':
      return <span>Bluebird</span>;
    case 'system':
      return <span>System</span>;
    default:
      return <span>{role}</span>;
  }
}

function PartView({
  part,
  renderMarkdown,
  highlight,
}: {
  part: UIMessage['parts'][number];
  renderMarkdown: boolean;
  highlight: boolean;
}) {
  // Tool invocations have a stable shape across `tool-*` types.
  if (isToolUIPart(part)) {
    return <ToolCallCard part={part} />;
  }

  switch (part.type) {
    case 'text':
      // Assistant messages render as Markdown (GFM tables, code blocks
      // with syntax highlighting). User messages render plain so the
      // user's literal input is never reinterpreted.
      if (renderMarkdown) {
        return <MarkdownRenderer source={part.text} highlight={highlight} />;
      }
      return (
        <p className="whitespace-pre-wrap leading-relaxed">{part.text}</p>
      );
    case 'reasoning':
      return (
        <details className="mt-1 rounded border border-white/10 bg-white/2 px-3 py-2 text-xs opacity-80">
          <summary className="cursor-pointer select-none opacity-70">
            Thinking…
          </summary>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{part.text}</pre>
        </details>
      );
    case 'source-url':
      return (
        <p className="text-xs">
          <a
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-dotted underline-offset-2 opacity-80 hover:opacity-100"
          >
            {part.title || part.url}
          </a>
        </p>
      );
    case 'file':
      return (
        <p className="text-xs opacity-80">
          File: <code translate="no">{part.filename ?? part.url}</code>
        </p>
      );
    case 'step-start':
      // step-start markers don't render — they're protocol bookkeeping
      // in the AI SDK message stream.
      return null;
    default:
      return null;
  }
}
